import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { PRODUCT_CATEGORY, SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { CurrencyCode } from '@schuettflix/utils';
import { Price } from '@schuettflix/conversion';
import { BalanceSheetProductProps } from '../hooks/useBalanceSheetCalculations';
import { FrancoIndicatorProductProps } from '@/shared/components/ProductGroupHeader/components/FrancoIndicator.tsx';

export interface BalanceProductPropsWithFallbackValues extends FrancoIndicatorProductProps, BalanceSheetProductProps {
    id: string;
}

export interface BalanceProductProps extends Partial<BalanceProductPropsWithFallbackValues> {
    category: BalanceProductPropsWithFallbackValues['category'];
    currencyCode?: CurrencyCode;
    id: string;
}

export function generateBalanceProductsWithFallbackValues(
    products: BalanceProductProps[]
): BalanceProductPropsWithFallbackValues[] {
    return products.map(product => ({
        id: product.id,
        category: product.category,
        salesPrice: product.salesPrice ? new Price(product.salesPrice, product.currencyCode ?? 'EUR').toNumber() : 0,
        purchasePrice: product.purchasePrice
            ? new Price(product.purchasePrice, product.currencyCode ?? 'EUR').toNumber()
            : 0,
        amount: product.amount ?? 0,
        unit: product.unit ?? 'TON',
    }));
}

interface UpsertParams {
    productId: string;
    productCategory: PRODUCT_CATEGORY;
    values: {
        salesPrice?: string;
        purchasePrice?: string;
        amount?: number;
        currencyCode?: CurrencyCode;
        unit?: SUPPORTED_UNITS;
    };
}

type BalanceSheetContextType = {
    balanceProducts: BalanceProductPropsWithFallbackValues[];
    upsertBalanceProduct: (upsertParams: UpsertParams) => void;
    deleteAllBalanceProducts: () => void;
    deleteBalanceProductById: (productId: string) => void;
};

const BalanceSheetContext = createContext<BalanceSheetContextType | null>(null);

export function useBalanceSheetContext(): BalanceSheetContextType {
    const context = useContext(BalanceSheetContext);
    if (!context) {
        throw new Error('BalanceSheetContext must be provided!');
    }
    return context;
}

export function BalanceSheetContextProvider({ children }: PropsWithChildren) {
    const [_balanceProducts, setBalanceProducts] = useState<BalanceProductProps[]>([]);

    const balanceProducts = useMemo(() => {
        return generateBalanceProductsWithFallbackValues(_balanceProducts);
    }, [_balanceProducts]);

    const deleteAllBalanceProducts = useCallback(() => {
        setBalanceProducts([]);
    }, []);

    const deleteBalanceProductById = useCallback((productId: string) => {
        setBalanceProducts(previousIntermediateOrderProducts =>
            previousIntermediateOrderProducts.filter(p => p.id !== productId)
        );
    }, []);

    const upsertBalanceProduct = useCallback(
        ({ productId, productCategory, values }: UpsertParams) => {
            setBalanceProducts(p => {
                const products = [...p];
                const index = products.findIndex(({ id }) => id === productId);
                const intermediateProduct = products[index];

                const quote = {
                    salesPrice: Number(values.salesPrice),
                    purchasePrice: Number(values.purchasePrice),
                    amount: values.amount,
                    currencyCode: values.currencyCode,
                    unit: values.unit,
                };

                if (intermediateProduct) {
                    products[index] = {
                        ...intermediateProduct,
                        ...quote,
                    };
                    return products;
                }

                return [...products, { category: productCategory, id: productId, ...quote }];
            });
        },
        [setBalanceProducts]
    );

    return (
        <BalanceSheetContext.Provider
            value={{ balanceProducts, upsertBalanceProduct, deleteAllBalanceProducts, deleteBalanceProductById }}
        >
            {children}
        </BalanceSheetContext.Provider>
    );
}
